import React from "react";
import { Box, Autocomplete, TextField, Typography } from "@mui/material";
import { useLanguage } from "../../i18n/LanguageContext";
import { FrenchFlag, BritishFlag } from "./FlagIcons";
import { LanguageCode } from "../../models/enums/LanguageCode";

interface LanguageOption {
	code: LanguageCode;
	label: string;
	flag: React.ComponentType;
}

const languageOptions: LanguageOption[] = [
	{
		code: LanguageCode.En,
		label: "English",
		flag: BritishFlag
	},
	{
		code: LanguageCode.Fr,
		label: "Français",
		flag: FrenchFlag
	}
];

interface LanguageSelectorProps {
	value?: LanguageCode;
	onChange?: (languageCode: LanguageCode) => void;
	/**
	 * When false, selecting an option only notifies onChange WITHOUT switching the UI language:
	 * the caller confirms first (e.g. the talent's "change your input language?" dialog) and
	 * applies the change itself. Default true (immediate switch — legacy behavior).
	 */
	applyLanguageOnSelect?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
	value,
	onChange,
	applyLanguageOnSelect = true
}) => {
	const { changeLanguage } = useLanguage();

	const currentOption =
		languageOptions.find(option => option.code === value) || languageOptions[0];

	const handleLanguageChange = (_: any, newValue: LanguageOption | null) => {
		if (newValue) {
			if (applyLanguageOnSelect) {
				changeLanguage(newValue.code);
			}
			onChange?.(newValue.code);
		}
	};

	return (
		<Box sx={{ display: "flex", width: "100%" }}>
			<Autocomplete
				value={currentOption}
				onChange={handleLanguageChange}
				options={languageOptions}
				getOptionLabel={option => option.label}
				isOptionEqualToValue={(option, value) => option.code === value.code}
				disableClearable
				sx={{ width: "400px" }}
				renderInput={params => (
					<TextField
						{...params}
						variant="outlined"
						slotProps={{
							input: {
								...params.InputProps,
								startAdornment: (
									<Box display="flex" alignItems="center" gap={2} sx={{ pl: 0.5 }}>
										<currentOption.flag />
										<Typography variant="body1"></Typography>
									</Box>
								)
							}
						}}
					/>
				)}
				renderOption={(props, option) => (
					<Box
						{...props}
						component="li"
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2.5,
							padding: "8px 16px !important"
						}}
					>
						<option.flag />
						<Typography variant="body1">{option.label}</Typography>
					</Box>
				)}
			/>
		</Box>
	);
};

export default LanguageSelector;
